import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react'
import { forwardRef, useId } from 'react'

import { cn } from '@/lib/utils/cn'

/**
 * The shared field frame: label above, help below, error below that, all wired
 * with aria-describedby and aria-invalid.
 *
 * Labels are never placeholders — a placeholder disappears the moment someone
 * starts typing, which is exactly when they need it.
 */
function FieldFrame({
  id,
  label,
  hint,
  error,
  required,
  children,
  className,
}: {
  id: string
  label?: string | undefined
  hint?: string | undefined
  error?: string | undefined
  required?: boolean | undefined
  children: ReactNode
  className?: string | undefined
}) {
  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label htmlFor={id} className="mb-2 block text-compact font-medium text-text">
          {label}
          {required && (
            <span aria-hidden="true" className="text-danger">
              {' '}
              *
            </span>
          )}
        </label>
      )}
      {children}
      {hint && !error && (
        <p id={`${id}-hint`} className="mt-2 text-caption text-text-tertiary">
          {hint}
        </p>
      )}
      {error && (
        <p
          id={`${id}-error`}
          className="mt-2 flex items-center gap-1 text-caption text-danger"
        >
          {/* An icon as well as colour: state is never signalled by colour alone. */}
          <span aria-hidden="true">⚠</span>
          {error}
        </p>
      )}
    </div>
  )
}

function describedBy(id: string, hint?: string, error?: string): string | undefined {
  const ids = [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean)
  return ids.length ? ids.join(' ') : undefined
}

const FIELD_BASE =
  'w-full rounded-control border bg-surface px-3 text-compact text-text ' +
  'transition-colors duration-control ease-standard ' +
  'placeholder:text-text-tertiary ' +
  'disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-surface-hover'

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string
  hint?: string
  error?: string
  leading?: ReactNode
  trailing?: ReactNode
  containerClassName?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, leading, trailing, className, containerClassName, required, ...props },
  ref,
) {
  const generated = useId()
  const id = props.id ?? generated

  return (
    <FieldFrame
      id={id}
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={containerClassName}
    >
      <div className="relative flex items-center">
        {leading && (
          <span
            aria-hidden="true"
            className="absolute left-3 text-text-tertiary pointer-events-none"
          >
            {leading}
          </span>
        )}
        <input
          ref={ref}
          id={id}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(id, hint, error)}
          className={cn(
            FIELD_BASE,
            'h-[44px]',
            error ? 'border-danger' : 'border-border-strong',
            // spacing-7 (40px): the icon sits at 12px and is ~16px wide.
            leading && 'pl-7',
            trailing && 'pr-7',
            className,
          )}
          {...props}
        />
        {trailing && (
          <span aria-hidden="true" className="absolute right-3 text-text-tertiary">
            {trailing}
          </span>
        )}
      </div>
    </FieldFrame>
  )
})

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  hint?: string
  error?: string
  containerClassName?: string
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, className, containerClassName, required, rows = 4, ...props },
  ref,
) {
  const generated = useId()
  const id = props.id ?? generated

  return (
    <FieldFrame
      id={id}
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={containerClassName}
    >
      <textarea
        ref={ref}
        id={id}
        rows={rows}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, hint, error)}
        className={cn(
          FIELD_BASE,
          'py-2 leading-normal resize-y',
          error ? 'border-danger' : 'border-border-strong',
          className,
        )}
        {...props}
      />
    </FieldFrame>
  )
})

export interface SelectOption {
  value: string
  label: string
}

export interface SelectProps
  extends Omit<InputHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string
  hint?: string
  error?: string
  options: SelectOption[]
  placeholder?: string
  containerClassName?: string
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label, hint, error, options, placeholder = 'Select…',
    className, containerClassName, required, value, ...props
  },
  ref,
) {
  const generated = useId()
  const id = props.id ?? generated

  return (
    <FieldFrame
      id={id}
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={containerClassName}
    >
      <select
        ref={ref}
        id={id}
        value={value}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, hint, error)}
        className={cn(
          FIELD_BASE,
          'h-[44px] pr-3',
          error ? 'border-danger' : 'border-border-strong',
          !value && 'text-text-tertiary',
          className,
        )}
        {...props}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </FieldFrame>
  )
})

export const Checkbox = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string }
>(function Checkbox({ label, hint, className, ...props }, ref) {
  const generated = useId()
  const id = props.id ?? generated

  return (
    <div className="flex items-start gap-3">
      <input
        ref={ref}
        id={id}
        type="checkbox"
        aria-describedby={hint ? `${id}-hint` : undefined}
        className={cn(
          'mt-0.5 size-4 shrink-0 rounded border-border-strong accent-action',
          'disabled:cursor-not-allowed disabled:opacity-60',
          className,
        )}
        {...props}
      />
      <span className="min-w-0">
        <label htmlFor={id} className="block text-compact text-text">
          {label}
        </label>
        {hint && (
          <span id={`${id}-hint`} className="mt-0.5 block text-caption text-text-tertiary">
            {hint}
          </span>
        )}
      </span>
    </div>
  )
})

/** A switch is a checkbox with a different affordance — role stays correct. */
export const Switch = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { label: string }
>(function Switch({ label, className, checked, ...props }, ref) {
  const generated = useId()
  const id = props.id ?? generated

  return (
    <label htmlFor={id} className="flex cursor-pointer items-center gap-3">
      <span className="relative inline-flex">
        <input
          ref={ref}
          id={id}
          type="checkbox"
          role="switch"
          checked={checked}
          className="peer sr-only"
          {...props}
        />
        <span
          aria-hidden="true"
          className={cn(
            'block h-[24px] w-[40px] rounded-pill bg-surface-active',
            'transition-colors duration-control ease-standard',
            'peer-checked:bg-action peer-disabled:opacity-60',
            'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2',
            'peer-focus-visible:outline-action',
            className,
          )}
        />
        <span
          aria-hidden="true"
          className={cn(
            'absolute top-1 left-1 size-4 rounded-pill bg-surface',
            'transition-transform duration-control ease-standard',
            'peer-checked:translate-x-4',
          )}
        />
      </span>
      <span className="text-compact text-text">{label}</span>
    </label>
  )
})
